import { QueryClient, QueryKey } from "@tanstack/react-query";

export interface QueryKeyMatchOptions {
    /**
     * Exact string match in any part of the query key
     */
    contains?: string;

    /**
     * Regex pattern to match against the query key
     */
    regex?: RegExp;

    /**
     * Match if query key starts with this string
     */
    startsWith?: string;

    /**
     * Match if query key ends with this string
     */
    endsWith?: string;

    /**
     * Custom predicate function for complex matching
     */
    predicate?: (queryKey: QueryKey) => boolean;
}

/**
 * Utility to find query keys in the cache that match certain patterns
 */
export function findQueryKeys(
    queryClient: QueryClient,
    matcher: QueryKeyMatchOptions,
): QueryKey[] {
    const queries = queryClient.getQueryCache().getAll();
    const matchingKeys: QueryKey[] = [];

    for (const query of queries) {
        const queryKey = query.queryKey;
        const queryKeyString = JSON.stringify(queryKey);

        let matches = false;

        if (matcher.contains && queryKeyString.includes(matcher.contains)) {
            matches = true;
        }

        if (matcher.regex && matcher.regex.test(queryKeyString)) {
            matches = true;
        }

        if (matcher.startsWith) {
            const firstKey = queryKey[0];
            if (
                typeof firstKey === "string" &&
                firstKey.startsWith(matcher.startsWith)
            ) {
                matches = true;
            }
        }

        if (matcher.endsWith) {
            const lastKey = queryKey[queryKey.length - 1];
            if (
                typeof lastKey === "string" &&
                lastKey.endsWith(matcher.endsWith)
            ) {
                matches = true;
            }
        }

        if (matcher.predicate && matcher.predicate(queryKey)) {
            matches = true;
        }

        if (matches) {
            matchingKeys.push(queryKey);
        }
    }

    return matchingKeys;
}

/**
 * Utility to update query data for all matching queries
 */
export function updateQueriesDataByPattern<T>(
    queryClient: QueryClient,
    matcher: QueryKeyMatchOptions,
    updater: (oldData: T | undefined) => T,
): void {
    const matchingKeys = findQueryKeys(queryClient, matcher);

    matchingKeys.forEach((queryKey) => {
        queryClient.setQueryData(queryKey, updater);
    });
}
